import { Box, Typography } from '@mui/material';
import { GridColDef } from '@mui/x-data-grid-pro';
import { useMemo } from 'react';

import { dataGridCol } from '@shared/components/grid/common';
import { formatCurrency, mapGridIndexes } from '@shared/commonFunctions';
import { PhoenixGrid } from '@shared/components/grid/PhoenixGrid';
import { Modal } from '@shared/components/modals/Modal';
import { PitchText } from '@styles/ContactManager/DealStyles';

import { IDeal, IProduct } from '../../interfaces';
import { TimeNumber } from './constants';

interface IPitchProps {
  products?: IProduct[];
  isPitchModalOpen: boolean;
  setPitchModalOpen: (isOpen: boolean) => void;
  deal?: IDeal;
}

const pitchColumns: GridColDef[] = [
  dataGridCol('product'),
  dataGridCol('total'),
  dataGridCol('costPerMonth'),
  dataGridCol('costPerDay'),
].map((i) => ({ ...i, flex: 1 }));

const roundAccurately = (value: number, decimalPlaces: number) =>
  Number(Math.round(Number(value + 'e' + decimalPlaces)) + 'e-' + decimalPlaces);

export const PitchModal = ({ products, isPitchModalOpen, setPitchModalOpen, deal }: IPitchProps) => {
  if (!isPitchModalOpen) return null;
  let sumOfRetail = 0;

  const dataRows = useMemo(() => {
    const loanTerm = deal?.financeConditionItem?.term ?? 1;
    const interestRate = (deal?.financeConditionItem?.rate && deal?.financeConditionItem.rate / 100) || 0;
    const monthlyInterest = interestRate / TimeNumber.MONTHS;
    const dailyInterest = interestRate / TimeNumber.DAYS;

    return products?.map(({ retail, productTypeName }) => {
      const COST_PER_MONTH = roundAccurately(
        (retail * (monthlyInterest * Math.pow(1 + monthlyInterest, loanTerm))) / (Math.pow(1 + monthlyInterest, loanTerm) - 1),
        2
      );

      const COST_PER_DAY = roundAccurately(
        (retail * (dailyInterest * Math.pow(1 + dailyInterest, loanTerm * 30))) /
          (Math.pow(1 + dailyInterest, loanTerm * 30) - 1),
        2
      );

      sumOfRetail += retail;

      return {
        product: productTypeName,
        total: '$' + retail,
        costPerMonth: formatCurrency(COST_PER_MONTH),
        costPerDay: formatCurrency(COST_PER_DAY),
      };
    });
  }, [
    products,
    deal?.financeConditionItem?.term,
    deal?.financeConditionItem?.rate,
    deal?.financeConditionItem?.totalAmountFinanced,
  ]);
  

  return (
    <Modal
      dialogWidth="800px" 
      title="Products Pitch"
      open={isPitchModalOpen} 
      onClose={() => setPitchModalOpen(false)}
    >
      <Box sx={{ padding: '20px'}}>
        <PhoenixGrid
          autoHeight
          hideFooter
          canNotCopy
          disableSelectionOnClick
          columns={pitchColumns}
          loading={false}
          rows={mapGridIndexes(dataRows)}
        />
        <PitchText>
          <Typography> Total Amount Financed w/o Product: </Typography>
          <Typography>${roundAccurately((deal?.financeConditionItem.totalAmountFinanced ?? 0) - sumOfRetail, 2)}</Typography>
        </PitchText>

        <PitchText>
          <Typography>Total Amount Financed: </Typography>
          <Typography>${roundAccurately((deal?.financeConditionItem.totalAmountFinanced ?? 0), 2)}</Typography>
        </PitchText>
      </Box>
    </Modal>
  );
};
